import React, { useMemo } from "react";
import SectionTitle from "../components/SectionTitle";
import Seo from "../components/Seo";
import { Clock, Users } from "lucide-react";
import { useData } from "../context/DataContext";
import { resolveImageUrl } from "../admin/ImageUpload";

const Cours = () => {
  const { courses, tenues, loaded } = useData();

  const grouped = useMemo(() => {
    const g = {};
    for (const c of courses) {
      const cat = c.category || "Autres";
      if (!g[cat]) g[cat] = [];
      g[cat].push(c);
    }
    return g;
  }, [courses]);

  if (!loaded) return <div className="min-h-[60vh] flex items-center justify-center text-stone-500">Chargement...</div>;

  return (
    <>
      <Seo title="Cours & Tenues — L'Élan Danse" description="Découvrez tous nos cours de danse : éveil, initiation, classique, élémentaire, supérieur et adultes. Tenues recommandées par discipline." />

      <section className="py-20 px-6">
        <SectionTitle kicker="Nos disciplines" title="Cours & Tenues" subtitle="De l'éveil corporel aux pointes, chaque élève trouve sa place et progresse à son rythme dans une ambiance studieuse et joyeuse." />

        <div className="max-w-6xl mx-auto space-y-16">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <div className="text-center mb-8">
                <span className="font-handwriting text-2xl text-deep-rose">{cat}</span>
                <div className="section-divider mt-3 !w-24" />
              </div>
              <div className="space-y-10">
                {list.map((c, idx) => (
                  <div key={c.id || c.slug} className={`grid md:grid-cols-2 gap-8 items-center ${idx % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""}`}>
                    <div className="overflow-hidden rounded-3xl shadow-md">
                      <img src={resolveImageUrl(c.image) || c.image} alt={c.name} className="w-full h-[340px] object-cover hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className={`${c.color || "bg-rose-100"} rounded-3xl p-8 md:p-10`}>
                      <span className="font-handwriting text-2xl text-deep-rose">{c.age}</span>
                      <h3 className="font-serif-display text-3xl md:text-4xl text-stone-800 mt-1">{c.name}</h3>
                      <div className="section-divider !mx-0 !w-20 my-4" />
                      <p className="text-stone-700 leading-relaxed font-serif-display text-lg">{c.desc}</p>
                      <div className="flex flex-wrap gap-5 mt-5 text-sm text-stone-600">
                        <span className="inline-flex items-center gap-2"><Clock size={16} className="text-deep-rose" /> {c.duration}</span>
                        <span className="inline-flex items-center gap-2"><Users size={16} className="text-deep-rose" /> {c.age}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="py-20 px-6 bg-blush-light/60">
        <SectionTitle kicker="À prévoir" title="Tenues recommandées" subtitle="Une tenue adaptée est essentielle pour le confort, la technique et l'esthétique du cours." />
        <div className="max-w-4xl mx-auto bg-white rounded-3xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-blush text-deep-rose">
              <tr>
                <th className="text-left font-serif-display text-lg p-5">Discipline</th>
                <th className="text-left font-serif-display text-lg p-5">Tenue requise</th>
                <th className="text-left font-serif-display text-lg p-5">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {tenues.map((t, i) => (
                <tr key={t.id || t.course} className={i % 2 === 0 ? "bg-cream" : "bg-white"}>
                  <td className="p-5 font-serif-display text-stone-800 align-top">{t.course}</td>
                  <td className="p-5 text-stone-600 leading-relaxed">{t.items}</td>
                  <td className="p-5 align-top text-right">
                    {t.orderUrl ? (
                      <a href={t.orderUrl} target="_blank" rel="noopener noreferrer"
                         data-testid={`tenue-order-btn-${t.id || i}`}
                         className="inline-flex items-center gap-1.5 bg-coral text-white px-4 py-2 rounded-full text-xs tracking-widest uppercase hover:bg-deep-rose transition-colors whitespace-nowrap">
                        {t.orderLabel || "Commander"} ↗
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-center text-stone-500 mt-6 italic font-serif-display">Les tenues peuvent être commandées directement auprès de l&apos;école en début de saison.</p>
      </section>
    </>
  );
};

export default Cours;
